import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Tooltip } from "antd";
import { pathOr } from "ramda";
import Linkify from "react-linkify";
import { If, Else, Then } from "react-if";
import { SegmentedMessage } from "sms-segments-calculator";
import { useFormikContext } from "formik";

import CustomInput from "../../../../components/customInput";
import CustomButton from "../../../../components/customButton";
import CommonService from "../../../../service/CommonService";
import CustomModal from "../../../../components/customModal";
import CommonInput from "../../../../components/commonFields";
import CustomLabel from "../../../../components/customLabel";
import { setPopup } from "../../../../actions/popupAction";
import EditTemplateModal from "./components/editTemplateModal";

import "./styles.scss";
import {
  pauseIcon,
  playIcon,
  InfoOutlineIcon,
  WarningFilledIcon,
} from "../../../../assets/svg";
import AudioUpload from "../../../../components/audioUpload";
import { debounceAsync, getFormattedMessage } from "../../../../helper/helpers";
import {
  SmallCrossIcon,
  SmallSaveIcon,
} from "../../../../assets/svg/mobileIcon";
import { MultipleFormikSelect } from "../../../../components/select/multipleSelect";
import { DEBOUNCE_TIME } from "../../../../constants/constants";
import { ERRORS } from "../../constants";

const SendMessage = (props) => {
  const commonService = new CommonService();
  const audioRef = props.audioRef;
  const { setFieldTouched, setFieldError } = useFormikContext();

  const {
    headers = {},
    setFieldValue = () => {},
    formValues = {},
    data = [],
    isTemplate = false,
    touched,
    errors,
    type,
    templates,
    listOfTemplates,
    parentState,
    subtype,
    name,
    tempName,
    selectedTempType,
    hasFieldVariables = true,
    saveChangesAllowed = false,
    fetchAccounts,
    allAccountsList = [],
    accountsList = [],
    showSegmentedArtifacts = true,
  } = props;

  const isIvrSmsMessage = ["message", "ivrMessage"].includes(name);

  const [state, setState] = useState({
    link: "",
    previewLoading: false,
    previewData: "",
    previewText: "",
    addVariables: false,
    saveTemplateModal: false,
    templateName: "",
    error: "",
    editTemplate: false,
    editTemplateModal: false,
    audioStatus: "Play",
    resetAudio: false,
    audioFileData: {},
    audioError: false,
    audioErrorMsg: "",
    characters: 0,
    words: 0,
    showAs: saveChangesAllowed && isIvrSmsMessage ? "default" : "custom",
  });

  const dispatch = useDispatch();

  const saveRef = useRef(false);

  const ref = useRef();

  const currentUser = useSelector((state) => state.login.user);

  const displayShowAs = saveChangesAllowed && isIvrSmsMessage ? true : false;

  const isTemplateModificationAllowed = ["OU", "TS"].includes(
    currentUser?.roles?.roleKey,
  );

  const { segmentDetails } = useMemo(
    () => {
      if (!showSegmentedArtifacts) {
        return { characters: "", segmentDetails: {} };
      }
      const fieldValue = state.previewText;
      const segmentObject = new SegmentedMessage(fieldValue || "");
      const segmentDetails = {
        nonGsmChars: segmentObject.getNonGsmCharacters(),
        encodingName: segmentObject.encodingName,
        segmentsCount: segmentObject.segmentsCount,
      };
      return {
        segmentDetails,
      };
    },
    [state.previewText, showSegmentedArtifacts],
  );

  const segmentedTooptip = () => (
    <div className="tooltip-content">
      <h3>Note on Character Count</h3>
      <p>
        The count of segments increases when a message contains Emoji, special
        characters, language characters, accent marks, and field variables.
        Replace field variables with actual values for an accurate segment
        count.
      </p>
      <p>
        Encoding:
        <span> {segmentDetails?.encodingName}</span>
      </p>
    </div>
  );

  const segmentedTooptipNonGSM = useMemo(
    () => {
      const uniqueNonGsmChars = Array.from(
        new Set(segmentDetails?.nonGsmChars || []),
      );
      return (
        <div className="tooltip-content">
          <h3>Note on Character Count</h3>
          {segmentDetails?.encodingName === "GSM-7" ? (
            <>
              <p>
                The message exceeds 1 segment. Messages longer than 1 segment
                may incur additional charges based on their length and content.
                Replace field variables with actual values for an accurate
                segment count.
              </p>
            </>
          ) : (
            <>
              <p>
                The message contains characters that increase the segment count.
                Please review these characters and update them as needed.
                Replace field variables with actual values for an accurate
                segment count.
              </p>
              <p className="tooltip-stats">
                Non GSM character count:
                <span className="bold-text">
                  {segmentDetails?.nonGsmChars?.length}
                </span>
              </p>
              <p className="tooltip-stats">
                Non GSM characters:
                {uniqueNonGsmChars.map((char) => (
                  <span className="bold-text" key={char}>
                    {char}
                  </span>
                ))}
              </p>
            </>
          )}
          {segmentDetails?.segmentsCount > 1 && (
            <p className="tooltip-stats">
              Segment count: <span>{segmentDetails?.segmentsCount}</span>
            </p>
          )}
          <p className="tooltip-stats">
            Encoding:
            <span> {segmentDetails?.encodingName}</span>
          </p>
        </div>
      );
    },
    [
      segmentDetails?.encodingName,
      segmentDetails?.nonGsmChars,
      segmentDetails?.segmentsCount,
    ],
  );

  useEffect(
    () => {
      const withoutBreaks = state.previewText.replace(/[\r\n]/gm, " ");
      setState((st) => ({
        ...st,
        characters: state.previewText.length,
        words:
          withoutBreaks.length > 0
            ? withoutBreaks
                .trim()
                .split(" ")
                .filter((elem) => elem.length > 0).length
            : 0,
      }));
    },
    [state.previewText],
  );

  const addText = (head) => {
    const header = `{${head}}`;
    const textArea = pathOr(
      null,
      ["current", "resizableTextArea", "textArea"],
      ref,
    );

    let text = formValues[name] + header;

    if (
      textArea &&
      (textArea.selectionStart || textArea.selectionStart === "0")
    ) {
      var startPos = textArea.selectionStart;
      var endPos = textArea.selectionEnd;
      text =
        textArea.value.substring(0, startPos) +
        header +
        textArea.value.substring(endPos, textArea.value.length);
    }

    setFieldValue(name, text);
  };

  useEffect(
    () => {
      setState((st) => ({
        ...st,
        addVariables: false,
        editTemplate: false,
      }));
      // Do not change anything in parent state
      parentState((st) => ({ ...st, templateInEditState: false }));
    },
    [isTemplate],
  );

  useEffect(
    () => {
      setState((st) => ({
        ...st,
        previewLoading: true,
      }));
      const formattedMessage = getFormattedMessage(
        formValues[name] || "",
        data,
        headers,
      );
      setState((st) => ({ ...st, previewText: formattedMessage }));
    },
    [formValues[name], data, headers],
  );

  const handleSaveTemplate = async (templateData) => {
    if (saveRef && saveRef.current) return;
    else {
      try {
        saveRef.current = true;

        const accountIdsSelected =
          isIvrSmsMessage && saveChangesAllowed
            ? formValues.accounts.map((value) => value.practiceId || "all")
            : null;

        const response = await commonService.saveTemplateData(
          templateData.template ? templateData.template : state.templateName,
          formValues[name],
          type,
          state.showAs,
          accountIdsSelected,
        );

        setState((st) => ({
          ...st,
          templateName: "",
          saveTemplateModal: false,
        }));

        setFieldValue("accounts", []);
        const successMessage = pathOr(
          "Sorry, could not update. Please refresh and try again!",
          ["data", "message"],
          response,
        );
        dispatch(
          setPopup({
            customPopupMessage: true,
            messageType: "success",
            message:
              successMessage.length > 50
                ? successMessage.slice(0, 51) + "... created successfully"
                : successMessage,
          }),
        );
        listOfTemplates();
        saveRef.current = false;
      } catch (err) {
        if (err?.response?.data?.message?.includes("already exists")) {
          setState((st) => ({
            ...st,
            error: err.response.data.message,
            //templateName: '',
          }));
        } else {
          const errorMessage = pathOr(
            "Something went wrong!",
            ["response", "data", "message"],
            err,
          );
          dispatch(
            setPopup({
              customPopupMessage: true,
              messageType: "error",
              message:
                errorMessage.length > 50
                  ? errorMessage.slice(0, 50) + "..."
                  : errorMessage,
            }),
          );
          setState((st) => ({
            ...st,
            templateName: "",
            saveTemplateModal: false,
          }));
        }

        saveRef.current = false;
      }
    }
  };

  const handleUpdateTemplate = async (templateData) => {
    if (saveRef && saveRef.current) return;
    else {
      try {
        saveRef.current = true;

        const accountIdsSelected = displayShowAs
          ? templateData.accounts.map((value) => value.practiceId || "all")
          : null;

        if (templateData.saveAs === "existing") {
          const payload = {
            templateMessage: formValues[name],
            type,
            ...(accountIdsSelected ? { accounts: accountIdsSelected } : {}),
          };
          const templateId = formValues[selectedTempType].key;
          const response = await commonService.editTemplate(
            payload,
            templateId,
          );
          dispatch(
            setPopup({
              customPopupMessage: true,
              messageType: "success",
              message: pathOr(
                "Something went wrong!",
                ["data", "message"],
                response,
              ),
            }),
          );
          setFieldValue(tempName, formValues[name]);
          listOfTemplates();

          setState((st) => ({
            ...st,
            editTemplateModal: false,
            addVariables: false,
            editTemplate: false,
          }));

          parentState((st) => ({ ...st, templateInEditState: false }));
        } else {
          const response = await commonService.saveTemplateData(
            templateData.template ? templateData.template : state.templateName,
            formValues[name],
            type,
            templateData.showAs,
            accountIdsSelected,
          );
          if (response.data.statusCode === 200) {
            setState((st) => ({
              ...st,
              templateName: "",
              saveTemplateModal: "",
              editTemplateModal: false,
              addVariables: false,
              editTemplate: false,
            }));
            listOfTemplates();
            parentState((st) => ({ ...st, templateInEditState: false }));
            setFieldValue(name, formValues?.tempMessage);
            dispatch(
              setPopup({
                customPopupMessage: true,
                messageType: "success",
                message: pathOr(
                  "Sorry, could not update. Please refresh and try again!",
                  ["data", "message"],
                  response,
                ),
              }),
            );
          }
        }
        saveRef.current = false;
      } catch (err) {
        if (err.response?.data?.message !== ERRORS.TEMPLATE.NAME_EXISTS) {
          setState((st) => ({ ...st, error: err.response.data.message }));
        }
        saveRef.current = false;
      }
    }
  };

  useEffect(
    () => {
      setState((st) => ({ ...st, templateName: state.templateName }));
    },
    [state.error],
  );

  const debouncedCheckTemplateExists = useCallback(
    debounceAsync(commonService.checkTemplateExists, DEBOUNCE_TIME, false),
    [commonService.checkTemplateExists],
  );

  useEffect(
    () => {
      // Call the API to check if the template name already exists.
      // This check is performed only if:
      // 1. The template name is not empty.
      // 2. Accounts are selected when the user is either OU or TS
      // 3. The message type is either SMS or IVR.
      if (
        !state.templateName.trim() ||
        (!formValues.accounts?.length && displayShowAs) ||
        !isIvrSmsMessage
      ) {
        handleRemoveError();
        return;
      }

      const accountIdsSelected = displayShowAs
        ? formValues.accounts?.map((value) => value.practiceId || "all")
        : [];

      debouncedCheckTemplateExists(state.templateName, type, accountIdsSelected)
        .then((response) => {
          if (response.data.exists) {
            setState((st) => ({
              ...st,
              error: ERRORS.TEMPLATE.NAME_EXISTS,
            }));
          } else {
            setState((st) => ({
              ...st,
              error: "",
            }));
          }
        })
        .catch((err) => {
          console.error(err);
          setState((st) => ({
            ...st,
            error: "Error checking template name",
          }));
        });
    },
    [state.templateName, formValues.accounts],
  );

  const handleOnChange = (e) => {
    const temp = e.target.value;
    setState((st) => ({
      ...st,
      templateName: temp,
    }));
  };

  const handleCancel = () => {
    setState((st) => ({
      ...st,
      saveTemplateModal: !state.saveTemplateModal,
      templateName: "",
      error: "",
    }));
  };

  const handleCancelChanges = () => {
    setState((st) => ({
      ...st,
      editTemplate: false,
      addVariables: false,
    }));
    setFieldValue(name, formValues[tempName]);
    parentState((st) => ({ ...st, templateInEditState: false }));
  };

  const handleRemoveError = () => {
    setState((st) => ({
      ...st,
      error: "",
    }));
  };

  const templateObj =
    templates &&
    templates.find(
      (item) =>
        item.id === formValues?.template.key ||
        item.id === formValues?.ivrTemplate.key ||
        item.id === formValues.optOutTemplate.key ||
        item.id === formValues.responseTemplate.key,
    );

  const selectedTemplateAccounts = state.editTemplateModal
    ? allAccountsList.filter((val) =>
        templateObj.accounts.includes(val.practiceId),
      )
    : [];

  if (state.editTemplateModal && templateObj.accounts[0] === "all") {
    selectedTemplateAccounts.push({ id: "all", name: "All" });
  }
  const isDefaultTemplate = templateObj?.showAs === "default" ? true : false;
  const canSaveEditedTemplate =
    (isTemplateModificationAllowed && !isDefaultTemplate) ||
    (!isTemplateModificationAllowed && isDefaultTemplate);
  const isSaveChangesDisabled =
    canSaveEditedTemplate ||
    (displayShowAs
      ? errors[name]
      : formValues[tempName].trim() === formValues[name].trim() ||
        errors[name]);

  const handlePlayPause = (value) => {
    if (value === "Play") {
      audioRef.current.play();
      setState((st) => ({
        ...st,
        audioStatus: "Pause",
      }));
    } else {
      audioRef.current.pause();
      setState((st) => ({
        ...st,
        audioStatus: "Play",
      }));
    }
  };

  const setAudioFileData = (file) => {
    setState((st) => ({
      ...st,
      audioFileData: file,
      audioStatus: "Play",
    }));
    setFieldValue("audio", file);
    props.setAudioRef(audioRef);
    audioRef.current && audioRef.current.pause();
    parentState((st) => ({ ...st, isAudioValid: false }));
  };
  const setAudioError = (error, msg) => {
    setState((st) => ({
      ...st,
      audioError: error,
      audioErrorMsg: msg,
    }));
    parentState((st) => ({ ...st, isAudioValid: error }));
  };
  const OnClearAudio = () => {
    setState((st) => ({
      ...st,
      audioFileData: {},
      audioStatus: "Play",
      resetAudio: !st.resetAudio,
      audioError: false,
      audioErrorMsg: "",
    }));
    setFieldValue("audio", "");
    audioRef.current && audioRef.current.pause();
  };

  useEffect(
    () => {
      OnClearAudio();
    },
    [subtype],
  );
  const toggleSaveTemplate = () => {
    setState((st) => ({
      ...st,
      saveTemplateModal: !state.saveTemplateModal,
      error: "",
    }));
  };

  const toggleAddVariables = () => {
    setState((st) => ({
      ...st,
      addVariables: !state.addVariables,
    }));
  };

  const clearMessage = () => {
    setFieldValue(name, "");
    setFieldValue(tempName, "");
    setFieldTouched(name, false, false);
    setFieldError(name, undefined);
  };

  const segmentText =
    showSegmentedArtifacts && formValues.convoType !== "ivr"
      ? `${`${state.characters > 0 ? segmentDetails.segmentsCount : 0} segment${
          segmentDetails.segmentsCount > 1 ? "" : ""
        } |`}`
      : "";

  const SegmentTooltip = () => {
    return (
      showSegmentedArtifacts &&
      (segmentDetails.segmentsCount < 2 &&
      segmentDetails.encodingName === "GSM-7" ? (
        <Tooltip
          overlayClassName="custom-tooltip-bg-dark"
          title={segmentedTooptip}
        >
          <span className="segmented-icon-container">
            <InfoOutlineIcon />
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          overlayClassName="custom-tooltip-bg-dark"
          title={segmentedTooptipNonGSM}
        >
          <span className="segmented-icon-container">
            <WarningFilledIcon
              withCircle={
                segmentDetails?.segmentsCount > 1 &&
                segmentDetails.encodingName !== "GSM-7"
              }
            />
          </span>
        </Tooltip>
      ))
    );
  };

  const DesktopMessageActions = () => {
    return (
      <div className="message-container-buttons">
        <div className="audio-buttons-wrapper">
          <CustomButton
            btntype="clear"
            title="Save as Template"
            onMouseDown={toggleSaveTemplate}
            disabled={/\S/.test(formValues[name]) ? false : true}
            className={/\S/.test(formValues[name]) ? "" : "disable-button"}
          />
        </div>

        <div className="right-buttons">
          <section className="characters">
            {`${segmentText} ${state.characters} char | ${state.words} words `}
            {showSegmentedArtifacts &&
              formValues.convoType !== "ivr" && <SegmentTooltip />}
          </section>
          {hasFieldVariables && (
            <div className="field-variables">
              <CustomButton
                btntype="clear"
                title={`${state.addVariables ? "-" : "+"} Add field variables`}
                onMouseDown={toggleAddVariables}
              />
              <div className="separator" />
            </div>
          )}
          <CustomButton
            btntype="clear"
            title="Reset"
            onMouseDown={clearMessage}
            disabled={/\S/.test(formValues[name]) ? false : true}
            className={/\S/.test(formValues[name]) ? "" : "disable-button"}
          />
        </div>
      </div>
    );
  };

  // Function used for mobile version
  const MobileMessageActions = () => {
    return (
      <>
        <div className="mobile-message-actions">
          {type === "opt-out" && formValues.enableOptOut
            ? false
            : true && (
                <div className="mobile-message-actions-variables">
                  <CustomButton
                    btntype="clear"
                    title={`${
                      state.addVariables ? "x " : "+ "
                    } Field variables`}
                    onMouseDown={toggleAddVariables}
                  />
                </div>
              )}
          <section className="characters mobile">
            {`${segmentText} ${state.characters} char | ${state.words} words `}
            {showSegmentedArtifacts &&
              formValues.convoType !== "ivr" && <SegmentTooltip />}
          </section>
          <div
            onMouseDown={(e) => {
              e.persist();
              if (/\S/.test(formValues[name])) {
                toggleSaveTemplate();
              }
            }}
            className={`mobile-message-actions-save ${
              /\S/.test(formValues[name]) ? "" : "disable-button"
            }`}
          >
            <SmallSaveIcon />
          </div>
        </div>
        <div className="mobile-message-clear" onMouseDown={clearMessage}>
          <SmallCrossIcon />
        </div>
      </>
    );
  };
  return (
    <section className="broadcast-form">
      {state.saveTemplateModal && (
        <CustomModal
          title="Save Message as Template"
          submitButtonText="Save Template"
          cancelButtonText="Cancel"
          handleDelete={handleSaveTemplate}
          handleCancel={handleCancel}
          isVisible={state.saveTemplateModal}
          disabled={
            /\S/.test(state.templateName) &&
            /^[A-Za-z0-9()@ _-]{1,255}$/.test(state.templateName) &&
            (displayShowAs ? formValues?.accounts?.length : true) &&
            !state.error
              ? false
              : true
          }
        >
          <div className="fields-wrapper">
            <section className="form-row save-template">
              <CustomLabel title="Template Name" required />
              <CommonInput
                placeholder="Enter Template Name"
                className={`custom-form-input ${
                  state.error ? "error-box" : ""
                }`}
                onChange={handleOnChange}
                value={state.templateName}
              />
              <div className="error-label mob-margin save-template-error">
                {state.error}
              </div>
            </section>
            {displayShowAs && (
              <section className="form-row">
                <MultipleFormikSelect
                  data={accountsList}
                  name="accounts"
                  optionLabelProp="name"
                  optionValueProp="id"
                  placeholder="Select account name"
                  required
                  title="Add to Accounts"
                  infiniteScroll
                  showAll={true}
                  loadMoreData={fetchAccounts}
                />
              </section>
            )}
          </div>
        </CustomModal>
      )}
      {state.editTemplateModal && (
        <EditTemplateModal
          title="Save Message as Template"
          submitButtonText="Save Template"
          cancelButtonText="Cancel"
          handleDelete={handleUpdateTemplate}
          handleRemoveError={handleRemoveError}
          handleCancel={() =>
            setState((st) => ({ ...st, editTemplateModal: false }))
          }
          isVisible={state.editTemplateModal}
          templateName={templateObj.templateName}
          responseError={state.error}
          user={templateObj?.user}
          showAs={isDefaultTemplate ? "default" : "custom"}
          accountsList={accountsList}
          fetchAccounts={fetchAccounts}
          selectedAccounts={selectedTemplateAccounts}
          displayShowAs={displayShowAs}
          formValues={formValues}
          tempName={tempName}
          name={name}
          type={type}
        />
      )}
      <div
        className={`messages-container ${
          subtype && formValues.convoType === "ivr"
            ? "voice-field-container"
            : ""
        }`}
      >
        <If condition={subtype && formValues.convoType === "ivr"}>
          <Then>
            <section className="send-message-with-variables-wrapper">
              {!Object.keys(state.audioFileData)?.length > 0 ? (
                <div className="message-container-buttons">
                  <div className="audio-buttons-wrapper">
                    <AudioUpload
                      audioRef={audioRef}
                      setAudioFileData={setAudioFileData}
                      handlePlayPause={handlePlayPause}
                      name="audio"
                      resetAudio={state.resetAudio}
                      setAudioError={setAudioError}
                    />
                  </div>
                </div>
              ) : (
                <div className={`send-message-container`}>
                  {state.audioFileData.size > 0 && !state.audioError ? (
                    <section className="audio-file-container">
                      <Button
                        className="audio-file"
                        onClick={() => handlePlayPause(state.audioStatus)}
                      >
                        <span className="icon">
                          {state.audioStatus === "Play" ? playIcon : pauseIcon}
                        </span>

                        <span
                          className="file-name"
                          title={state?.audioFileData?.name}
                        >
                          {state?.audioFileData?.name}
                        </span>
                      </Button>

                      <Button className="audio-close" onClick={OnClearAudio}>
                        x
                      </Button>
                    </section>
                  ) : null}
                </div>
              )}
            </section>
            {state.audioError && (
              <div className="form-row">
                <p className="error-text">{state.audioErrorMsg}</p>
              </div>
            )}
          </Then>
          <Else>
            <section className="send-message-with-variables-wrapper">
              <div
                className={`send-message-container ${
                  isTemplate && !state.editTemplate ? "disabled" : ""
                } ${touched[name] && errors[name] ? "error-box" : ""}`}
              >
                <If condition={isTemplate}>
                  <Then>
                    <If condition={state.editTemplate}>
                      <Then>
                        <CustomInput
                          inputRef={ref}
                          type="textarea"
                          name={name}
                          resize="none"
                          placeholder="Type message here..."
                          className="send-message-textarea"
                        />
                      </Then>
                      <Else>
                        <div className="content">{formValues[name]}</div>
                        <section className="characters onlymob">
                          {`${segmentText} ${state.characters} char | ${
                            state.words
                          } words `}
                          {showSegmentedArtifacts &&
                            formValues.convoType !== "ivr" && (
                              <SegmentTooltip />
                            )}
                        </section>
                      </Else>
                    </If>

                    <div className="message-container-buttons">
                      <If condition={state.editTemplate}>
                        <Then>
                          <div className="left-buttons">
                            <CustomButton
                              btntype="clear"
                              title="Cancel"
                              onClick={() => handleCancelChanges()}
                              className="cancel-button"
                            />

                            <div className="separator" />
                            <CustomButton
                              btntype="clear"
                              title="Save Changes"
                              onClick={() => {
                                if (isSaveChangesDisabled) return;
                                setState((st) => ({
                                  ...st,
                                  editTemplateModal: true,
                                  error: "",
                                }));
                              }}
                              disabled={isSaveChangesDisabled}
                              className={
                                isSaveChangesDisabled ? "disable-button" : ""
                              }
                            />
                          </div>

                          <div className="right-buttons">
                            {hasFieldVariables && (
                              <>
                                <section className="characters">
                                  {`${segmentText} ${state.characters} char | ${
                                    state.words
                                  } words `}
                                  {showSegmentedArtifacts &&
                                    formValues.convoType !== "ivr" && (
                                      <SegmentTooltip />
                                    )}
                                </section>
                                <CustomButton
                                  btntype="clear"
                                  title={`${
                                    state.addVariables ? "-" : "+"
                                  } Add field variables`}
                                  onClick={() =>
                                    setState((st) => ({
                                      ...st,
                                      addVariables: !state.addVariables,
                                    }))
                                  }
                                />
                                <div className="separator" />
                              </>
                            )}
                            <CustomButton
                              btntype="clear"
                              title="Reset"
                              onClick={() => {
                                setFieldValue(name, "");
                              }}
                              disabled={
                                /\S/.test(formValues[name]) ? false : true
                              }
                              className={
                                /\S/.test(formValues[name])
                                  ? ""
                                  : "disable-button"
                              }
                            />
                          </div>
                        </Then>
                        <Else>
                          {formValues[selectedTempType] && (
                            <>
                              <CustomButton
                                btntype="clear"
                                title="Edit Template Message"
                                onClick={() => {
                                  setState((st) => ({
                                    ...st,
                                    editTemplate: true,
                                  }));
                                  parentState((st) => ({
                                    ...st,
                                    templateInEditState: true,
                                  }));
                                }}
                                className="edit-template-button"
                              />
                              <section className="characters">
                                {`${segmentText} ${state.characters} char | ${
                                  state.words
                                } words `}
                                {showSegmentedArtifacts &&
                                  formValues.convoType !== "ivr" && (
                                    <SegmentTooltip />
                                  )}
                              </section>
                            </>
                          )}
                        </Else>
                      </If>
                    </div>
                  </Then>

                  <Else>
                    <>
                      <CustomInput
                        inputRef={ref}
                        type="textarea"
                        name={name}
                        resize="none"
                        placeholder="Type message here..."
                        className="send-message-textarea"
                        errorStyle="static"
                      />

                      <DesktopMessageActions />
                      <MobileMessageActions />
                    </>
                  </Else>
                </If>
              </div>

              <If condition={state.addVariables}>
                <Then>
                  <section className="custom-modal-search">
                    <section className="custom-modal-tags-wrapper">
                      <div className="tags-container">
                        {headers.map((header, index) => (
                          <Tooltip
                            destroyTooltipOnHide
                            title={header.Header}
                            key={`tag-no-${index}`}
                          >
                            <p
                              key={`tag-no-${index}`}
                              className="tags"
                              onClick={() => {
                                //addText(header.accessor);
                                addText(header.Header);
                              }}
                            >
                              {header.Header}
                            </p>
                          </Tooltip>
                        ))}
                      </div>
                    </section>
                  </section>
                </Then>
              </If>
            </section>

            <div className="preview-message-container">
              <div
                className={`content ${state.previewText && "preview-content"}`}
              >
                <If condition={state.previewText}>
                  <Then>
                    <>
                      <div className="text">
                        <Linkify>{state.previewText}</Linkify>
                        {name !== "optOutMessage" &&
                          name !== "smsIvrMessage" && (
                            <div>
                              <br />
                              {formValues.optOutMessage}
                            </div>
                          )}
                      </div>
                    </>
                  </Then>

                  <Else>
                    <div className="empty-text">
                      Response message preview will show up here
                    </div>
                  </Else>
                </If>
              </div>
            </div>
          </Else>
        </If>
      </div>
    </section>
  );
};

export default SendMessage;
